/**
 * EditarRecursoPresupuestoModal
 * Modal para editar un recurso existente en una partida de presupuesto
 */

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { toast } from 'react-toastify';
import './EditarRecursoPresupuestoModal.css';

const EditarRecursoPresupuestoModal = ({ recurso, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    cantidad_presupuesto: '',
    precio_unitario_costo: '',
    precio_unitario_venta: '',
    markup: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (recurso) {
      setFormData({
        nombre: recurso.nombre || '',
        descripcion: recurso.descripcion || '',
        cantidad_presupuesto: recurso.cantidad_presupuesto || '',
        precio_unitario_costo: recurso.precio_unitario_costo || '',
        precio_unitario_venta: recurso.precio_unitario_venta || '',
        markup: recurso.markup || ''
      });
    }
  }, [recurso]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateSubtotal = () => {
    const cantidad = parseFloat(formData.cantidad_presupuesto) || 0;
    const precio = parseFloat(formData.precio_unitario_venta) || 0;
    return (cantidad * precio).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre del recurso es requerido');
      return;
    }

    if (!formData.cantidad_presupuesto || parseFloat(formData.cantidad_presupuesto) <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    if (!formData.precio_unitario_venta || parseFloat(formData.precio_unitario_venta) < 0) {
      toast.error('El precio de venta es requerido y debe ser mayor o igual a 0');
      return;
    }

    setLoading(true);
    try {
      // Recurso.id es el ID de PresupuestoVentaRecurso (tabla intermedia)
      await presupuestoHierarchyService.updateVentaRecurso(recurso.id, {
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim(),
        cantidad_presupuesto: parseFloat(formData.cantidad_presupuesto),
        precio_unitario_costo: parseFloat(formData.precio_unitario_costo) || 0,
        precio_unitario_venta: parseFloat(formData.precio_unitario_venta),
        markup: parseFloat(formData.markup) || 0
      });

      toast.success('Recurso actualizado exitosamente');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating recurso:', error);
      toast.error('Error al actualizar el recurso');
    } finally {
      setLoading(false);
    }
  };

  if (!recurso) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Editar Recurso</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="nombre">Nombre del Recurso *</label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              placeholder="Ej: Diseño de portada"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="descripcion">Descripción</label>
            <textarea
              id="descripcion"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              placeholder="Descripción detallada del recurso (opcional)"
              disabled={loading}
              rows="2"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cantidad_presupuesto">Cantidad *</label>
              <input
                type="number"
                id="cantidad_presupuesto"
                name="cantidad_presupuesto"
                value={formData.cantidad_presupuesto}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="precio_unitario_venta">Precio Venta *</label>
              <input
                type="number"
                id="precio_unitario_venta"
                name="precio_unitario_venta"
                value={formData.precio_unitario_venta}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="precio_unitario_costo">Precio Costo</label>
              <input
                type="number"
                id="precio_unitario_costo"
                name="precio_unitario_costo"
                value={formData.precio_unitario_costo}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="markup">Markup (%)</label>
              <input
                type="number"
                id="markup"
                name="markup"
                value={formData.markup}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                max="100"
                disabled={loading}
              />
            </div>
          </div>

          {formData.cantidad_presupuesto && formData.precio_unitario_venta && (
            <div className="subtotal-preview">
              <span>Subtotal estimado:</span>
              <span className="subtotal-value">${calculateSubtotal()}</span>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarRecursoPresupuestoModal;
